<script lang="ts">
  import { ceiling, floor } from '../core/engine';
  import { famousAt } from '../core/famous';
  import { isPrime, nextPrime } from '../core/numbers';
  import { fmt } from '../shared/lib/format';
  import { game } from '../shared/lib/game.svelte';

  /**
   * The number line in characters, roguelike style: @ is you, * a prime, + a famous number,
   * # the edge. Ground you have walked is drawn in full ink, the rest faintly.
   */
  const HALF = 15;
  const WIDTH = HALF * 2 + 1;

  const s = $derived(game.s);

  const cells = $derived.by(() => {
    const out: Array<{ ch: string; kind: string; seen: boolean }> = [];
    for (let n = s.position - HALF; n <= s.position + HALF; n++) {
      const seen = n >= s.minVisited && n <= s.maxVisited;
      if (n === s.position) out.push({ ch: '@', kind: 'you', seen: true });
      else if (n > ceiling(s) || n < floor(s)) out.push({ ch: '#', kind: 'wall', seen });
      else if (isPrime(n)) out.push({ ch: '*', kind: 'prime', seen });
      else if (famousAt(n).length) out.push({ ch: '+', kind: 'famous', seen });
      else out.push({ ch: '·', kind: 'plain', seen });
    }
    return out;
  });

  /** A second row with numbers written under every multiple of ten that fits. */
  const labels = $derived.by(() => {
    const row = Array<string>(WIDTH).fill(' ');
    for (let i = 0; i < WIDTH; i++) {
      const n = s.position - HALF + i;
      if (n % 10 !== 0) continue;
      const text = String(n);
      if (i + text.length > WIDTH) continue;
      if (row.slice(Math.max(0, i - 1), i + text.length + 1).some((c) => c !== ' ')) continue;
      for (let j = 0; j < text.length; j++) row[i + j] = text[j]!;
    }
    return row.join('');
  });

  const ahead = $derived(nextPrime(s.position, s.direction));
</script>

<figure class="map" aria-label="Map of the number line around you">
  <pre aria-hidden="true"><span class="row"
      >{#each cells as c, i (i)}<span class="c {c.kind}" class:seen={c.seen}>{c.ch}</span
        >{/each}</span
    ><span class="row labels">{labels}</span></pre>
  <figcaption>
    {#if ahead !== null && ahead <= ceiling(s)}
      next prime: <span class="num">{fmt(ahead)}</span>, {fmt(Math.abs(ahead - s.position))} away.
    {:else}
      no primes that way.
    {/if}
  </figcaption>
</figure>

<style>
  .map {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  pre {
    margin: 0;
    font-family: var(--mono);
    /* 31 columns across the narrowest phones without wrapping. */
    font-size: clamp(11px, calc((100vw - 48px) / 19.5), 17px);
    line-height: 1.5;
    overflow: hidden;
    white-space: pre;
  }
  .row {
    display: block;
  }
  .c {
    color: var(--faint);
  }
  .c.seen {
    color: var(--ink);
  }
  .c.prime {
    font-weight: 700;
  }
  .c.you {
    font-weight: 800;
    color: var(--ink);
  }
  .labels {
    /* Same size as the map row, or the columns stop lining up. */
    color: var(--faint);
  }
  figcaption {
    font-size: 0.95rem;
    color: var(--dim);
  }
</style>
